import { INPUT_TYPES } from 'app-constants/inputTypes';
import { getGlobalVariables, getGlobalFunctions } from '../selectors';
import { botspecsActions } from '../reducer';
import { updateParsingBotspec, postBotspec } from './chatbots';
import { createSpecParser, createParseProdJson, nextTransitionIdFor } from './specParser/core';
import slugify from './utils';
import { BOT_UPDATE } from 'utils/toastDispacher';
import logger from 'utils/logger';

// Parser del spec 1.0 (lo ejecuta twnel-cf-api-botcore). La lógica común está en
// specParser/core.js; aquí solo lo propio de esta versión.

/**
 * Limpia el título del nodo, removiendo el ID si existe
 * Ejemplo: "Send Message#1" -> "Send Message"
 */
export const cleanNodeTitle = (title) => {
  if (!title) return '';
  // Remover el #ID del final si existe
  return title.replace(/#\d+$/, '');
};

const nextTransitionId = nextTransitionIdFor(cleanNodeTitle);

const parseGenericInputType = (property) => ({
  ...property,
  data: [
    property.data.placeholder && { placeholder: property.data.placeholder },
  ],
  method: 'keyboard',
});

const parseNumberInputType = (property) => {
  const {
    placeholder,
    integer,
    min,
    max,
  } = property?.data || {};
  return {
    ...property,
    data: [
      { placeholder },
      { integer },
      { min },
      { max },
    ],
    method: 'keyboard',
  };
};

const putVariableName = (variable) => (variable.includes('.') ? `{{ variables.${variable} | to_json_str }}` : `{{ variables.session.${variable} | to_json_str }}`);

const parseRadioCheckboxInputType = (property) => {
  // If optionsVariable is defined, use it to get the options from a variable
  const data = property.optionsVariable && property.optionsVariable.length > 0
    ? putVariableName(property.optionsVariable)
    : Object.keys(property.data).reduce(
      (accum, key) => [
        ...accum, { id: property.data[key].value, label: property.data[key].name },
      ], [],
    );

  return {
    type: property.type,
    data,
    method: 'keyboard',
    ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
  };
};

const parseDateInputType = (property) => {
  const date = new Date(property.data.default);
  return {
    type: 'date',
    data: [
      { format: property.data.format },
      { placeholder: property.data.format },
      {
        default: property.data.useDefaultDate
          ? {} : { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() },
      },
    ],
    method: 'keyboard',
    ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
  };
};

const parsePasswordLocationInputType = (property) => ({
  ...property,
  data: Object.keys(property.data).reduce(
    (accum, key) => [
      ...accum, { [key]: property.data[key] },
    ], [],
  ),
  method: 'keyboard',
});

const parseMediaInputType = (property) => ({
  ...property,
  geotag: property.data.geotag,
  data: [
    property.data.media_type && { media_type: property.data.media_type },
    ...property.data.picker ? [{ picker: property.data.picker }] : [],
    ...property.data.camera ? [{ facing: property.data.camera }] : [],
  ],
  method: 'keyboard',
});

const parseBarcodeInputType = (property) => {
  /** @type {Record<string, any>} */
  const data = {
    type: 'barcode',
    data: [{ response: property.data.response }],
    geotag: property.data.geotag,
    method: 'keyboard',
    ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
  };
  if (property.data.subtype) {
    data.subtype = property.data.subtype;
  }
  return data;
};

const parseSignatureInputType = (property) => ({
  type: 'signature',
  data: [{ caption: property.data.caption }],
  geotag: property.data.geotag,
  method: 'keyboard',
  ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
});

const parseInputType = (property) => {
  const parseInputType = {
    [INPUT_TYPES.TEXT]: parseGenericInputType,
    [INPUT_TYPES.NUMBER]: parseNumberInputType,
    [INPUT_TYPES.PHONE]: parseGenericInputType,
    [INPUT_TYPES.EMAIL]: parseGenericInputType,
    [INPUT_TYPES.RADIO]: parseRadioCheckboxInputType,
    [INPUT_TYPES.CHECKBOX]: parseRadioCheckboxInputType,
    [INPUT_TYPES.DATE]: parseDateInputType,
    [INPUT_TYPES.PASSWORD]: parsePasswordLocationInputType,
    [INPUT_TYPES.LOCATION]: parsePasswordLocationInputType,
    [INPUT_TYPES.MEDIA]: parseMediaInputType,
    [INPUT_TYPES.BARCODE]: parseBarcodeInputType,
    [INPUT_TYPES.SIGNATURE]: parseSignatureInputType,
  };

  return parseInputType[property.type](property);
};

const parseCallApiAction = (properties, nodeId, nodes) => {
  const HTTP_OK = 200;
  const DEFAULT_SCHEMA = {};

  const params = Object.keys(properties.params).reduce((accum, key) => {
    const values = [...Object.values(properties.params[key])];
    const paramName = values[0];
    const paramValue = values[1];

    if (accum[paramName]) {
      if (Array.isArray(accum[paramName])) {
        return { ...accum, [paramName]: [...accum[paramName], paramValue] };
      }
      return { ...accum, [paramName]: [accum[paramName], paramValue] };
    }

    return { ...accum, [paramName]: paramValue };
  }, {});

  const headers = Object.keys(properties.headers).reduce((accum, key) => {
    const values = [...Object.values(properties.headers[key])];
    return { ...accum, [values[0]]: values[1] };
  }, {});

  const { connections } = nodes[nodeId].outputs.on_error;
  let onErrorNextTransition = '';

  if (connections.length > 0) {
    onErrorNextTransition = nextTransitionId(nodes, connections);
  }

  const {
    type, vars, method, url, body, postVerify, contentType,
    integration, integrationService, timeout,
  } = properties;

  const status = postVerify.status ? postVerify.status.map((code) => parseInt(code.split(' ')[0], 10)) : HTTP_OK;

  return {
    type,
    vars,
    eval: {
      method: method.toUpperCase(),
      content_type: contentType,
      url,
      params: JSON.stringify(params),
      headers,
      ...body.length > 2 && { body: JSON.stringify(body) },
      integration,
      integration_service: integrationService,
      timeout: parseInt(timeout || '0', 10),
    },
    post_eval: {
      verify: {
        ...contentType === 'application/json' ? { schema: postVerify.schema ? JSON.parse(postVerify.schema) : DEFAULT_SCHEMA } : {},
        status,
        timecap: parseInt(postVerify.timecap || '0', 10),
      },
    },
    ...onErrorNextTransition.length > 0 && { on_error: { next: onErrorNextTransition } },
  };
};

const parser = createSpecParser({
  // En v1 las claves de transición y acción usan el título sin `#N`.
  titleKey: cleanNodeTitle,
  uniqueImplicitTransitions: true,
  rulesTitle: cleanNodeTitle,
  rulesActionTitle: cleanNodeTitle,
  parseInputType,
  parseCallApiAction,
  callFunctionInput: (input) => input,
  mapResultFrom: (path) => `{{ ${path} }}`,
  radioOptionValue: (option) => option.name,
  implicitConditionalStep: {
    transitionId: (title, nodeId) => `transition.${slugify(`${cleanNodeTitle(title)}${nodeId}`)}.output.answer.id`,
    actionKey: (nodeId) => slugify(`conditional_step_implicit_${nodeId}_action`),
  },
});

export const { parseNodeTransitions, parseNodeActions, markEntryPoint } = parser;
const { parseNodes } = parser;

export const fetchGlobalVariablesInternal = () => (dispatch, getState, getContext) => {
  const state = getState();
  const cachedVariables = getGlobalVariables(state);

  // Si ya tenemos variables en caché, retornarlas inmediatamente
  if (Object.keys(cachedVariables).length > 0) {
    return Promise.resolve(cachedVariables);
  }

  // Si no hay caché, hacer la petición directamente
  const { api } = getContext();
  dispatch(botspecsActions.loadingGlobalVariables(true));

  return api.variables.list()
    .then((variables) => {
      // Convertir el array de variables a un objeto con el nombre como key
      const variablesObject = variables.reduce((acc, variable) => {
        acc[variable.name] = variable;
        return acc;
      }, {});

      dispatch(botspecsActions.updateGlobalVariables(variablesObject));

      return variablesObject;
    })
    .catch((error) => {
      dispatch(botspecsActions.loadingGlobalVariables(false));
       
      logger.error('Error fetching global variables:', error);
      return {};
    });
};

export const fetchGlobalFunctionsInternal = () => (dispatch, getState, getContext) => {
  const state = getState();
  const cachedFunctions = getGlobalFunctions(state);

  // Si ya tenemos funciones en caché, retornarlas inmediatamente
  if (Object.keys(cachedFunctions).length > 0) {
    return Promise.resolve(cachedFunctions);
  }

  // Si no hay caché, hacer la petición directamente
  const { api } = getContext();
  dispatch(botspecsActions.loadingGlobalFunctions(true));

  return api.functions.list()
    .then((functions) => {
      // Convertir el array de funciones a un objeto con el nombre como key
      const functionsObject = functions.reduce((acc, func) => {
        acc[func.name] = func;
        return acc;
      }, {});

      dispatch(botspecsActions.updateGlobalFunctions(functionsObject));

      return functionsObject;
    })
    .catch((error) => {
      dispatch(botspecsActions.loadingGlobalFunctions(false));
       
      logger.error('Error fetching global functions:', error);
      return {};
    });
};

export const parseProdJson = createParseProdJson(
  parseNodes, [fetchGlobalVariablesInternal, fetchGlobalFunctionsInternal],
);

export const updateBotspecsVariable = (botspec) => (dispatch) => {
  dispatch(updateParsingBotspec(true));
  Promise.all([
    dispatch(fetchGlobalVariablesInternal()),
    dispatch(fetchGlobalFunctionsInternal()),
  ])
    .then(([globalVariables, globalFunctions]) => {
      const parsedSpecJson = parseNodes(
        botspec.spec_json,
        botspec.rete_json,
        globalVariables || {},
        globalFunctions || {},
      );

      dispatch(
        postBotspec({
          ...botspec,
          spec_json: parsedSpecJson,
          rete_json: botspec.rete_json,
        }, {
          actionId: BOT_UPDATE,
          elementName: botspec.name,
        }),
      ).then(() => {
        dispatch(updateParsingBotspec(false));
      });
    })
    .catch(() => {
      dispatch(updateParsingBotspec(false));
    });
};

export default parseNodes;
